import React, { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, Percent, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Small host-facing card that shows what commission the platform will keep
 *  on the host's next paid booking. New hosts get 0 % for the first 90 days;
 *  after that it flips to the standard rate. Admins can override any host. */
export default function HostCommissionCard() {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    axios.get(`${API}/host/commission`).then(({ data }) => setStatus(data)).catch(() => setStatus(null));
  }, []);

  if (!status) return null;

  const pct = (r) => `${(Number(r || 0) * 100).toFixed(r < 0.01 ? 2 : 1)}%`;
  const isFree = status.is_in_free_period && status.source !== "override";
  const days = status.days_left ?? null;

  if (isFree) {
    return (
      <div
        data-testid="host-commission-card"
        className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-forest/20 bg-forest/5 p-4"
      >
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-forest/10">
            <Sparkles className="h-4 w-4 text-forest" />
          </span>
          <div>
            <div className="font-semibold text-slate-900">🎉 You&apos;re in your commission-free window</div>
            <div className="text-xs text-slate-600">
              EasyStay keeps <b>0 %</b> commission on your first {status.free_period_days} days — every kwacha guests pay lands with you.
            </div>
          </div>
        </div>
        {days !== null && (
          <div className="rounded-xl bg-white px-3 py-2 text-right shadow-sm" data-testid="host-commission-countdown">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Days left</div>
            <div className="font-display text-xl font-bold text-forest">{days}</div>
            <div className="mt-0.5 text-[10px] text-slate-500">
              Reverts to {pct(status.standard_rate)} on {status.free_until ? new Date(status.free_until).toLocaleDateString() : "?"}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid="host-commission-card"
      className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4"
    >
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-copper/10">
        <Percent className="h-4 w-4 text-copper" />
      </span>
      <div className="flex-1">
        <div className="font-semibold text-slate-900">
          Current commission: {pct(status.rate)}
          {status.source === "override" && (
            <span className="ml-2 rounded-full bg-copper/10 px-2 py-0.5 text-[10px] font-semibold text-copper">Custom rate</span>
          )}
        </div>
        <div className="text-xs text-slate-500">
          {status.source === "override"
            ? "This rate was set for you by an EasyStay admin."
            : `The standard EasyStay commission is ${pct(status.standard_rate)} per paid booking.`}
        </div>
      </div>
    </div>
  );
}
